import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, User, Clock, Activity, Filter, Download, Eye, Trash2, Edit, Copy, Move } from 'lucide-react';
import fileService from '../services/fileService';
import toast from 'react-hot-toast';
import { TableLoader } from './CommonLoader';

const FileAccessLogs = ({ isOpen, onClose, file, dataroomUuid }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0
  });
  const [filters, setFilters] = useState({
    action: '',
    user_id: ''
  });

  useEffect(() => {
    if (isOpen && file) {
      fetchAccessLogs();
    }
  }, [isOpen, file, pagination.page, filters]);

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...(filters.action && { action: filters.action }),
        ...(filters.user_id && { user_id: filters.user_id })
      };

      const response = await fileService.getFileAccessLogs(dataroomUuid, file.uuid, params);
      
      
      // Ensure logs is always an array
      const logsData = response.data || response.logs || [];
      setLogs(Array.isArray(logsData) ? logsData : []);
      
      setPagination(prev => ({
        ...prev,
        total: response.total || response.pagination?.total || 0
      }));
    } catch (error) {
      toast.error('Failed to fetch access logs');
      setLogs([]); // Ensure logs is an empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      view: <Eye className="w-4 h-4" />,
      download: <Download className="w-4 h-4" />,
      delete: <Trash2 className="w-4 h-4" />,
      rename: <Edit className="w-4 h-4" />,
      copy: <Copy className="w-4 h-4" />,
      move: <Move className="w-4 h-4" />
    };
    return icons[action] || <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    const colors = {
      view: 'bg-blue-100 text-blue-700',
      download: 'bg-green-100 text-green-700',
      delete: 'bg-red-100 text-red-700',
      rename: 'bg-yellow-100 text-yellow-700',
      copy: 'bg-purple-100 text-purple-700',
      move: 'bg-indigo-100 text-indigo-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.per_page);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Access Logs</h2>
            <p className="text-sm text-gray-500 mt-1">{file?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="mb-6 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Actions</option>
                <option value="view">View</option>
                <option value="download">Download</option>
                <option value="rename">Rename</option>
                <option value="copy">Copy</option>
                <option value="move">Move</option>
                <option value="delete">Delete</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID
              </label>
              <input
                type="text"
                placeholder="Filter by user ID"
                value={filters.user_id}
                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Logs Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <TableLoader rows={5} columns={4} />
            ) : logs.length === 0 ? (
              <div className="text-center py-16">
                <Activity className="mx-auto h-16 w-16 text-gray-300" />
                <p className="mt-4 text-gray-500">No access logs found</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log, index) => {
                      // Debug log to see the structure of each log entry
                      if (index === 0) {
                      }
                      return (
                      <tr key={log.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.user_name || log.user?.name || log.userName || log.user?.user_name || 'Unknown User'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {log.user_email || log.user?.email || log.userEmail || log.user?.user_email || log.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                            {getActionIcon(log.action)}
                            <span className="ml-1">{log.action}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.ip_address || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-1" />
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                      {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                      {pagination.total} results
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        disabled={pagination.page === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        disabled={pagination.page === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FileAccessLogs;